import * as React from "react";
import {useEffect, useState, useRef} from "react";
import {grayLogApi} from "../../../util/grayLog/grayLogApi";
import {Button, Checkbox, Icon, Input, Popup} from "semantic-ui-react";
import {IfBox} from "../../../style/if";
import {LogTextOutput} from "./LogTextOutput";
import {ConfigContext} from "../../context/ConfigContext";
import {authentication} from "../../../../authentication";
import {
    getGraylogConnectionColorByMilliseconds,
    getGraylogDelay,
    GrayLogStatus
} from "../systemStatus/healthAndVersion/GrayLogStatus";

export interface LogViewProps {
    query: string
    initialTimespan?: number
    origin: "exportSim"
        | "importSim"
        | "transportOrderConfigTest"
        | "matrixDoc"
        | "multiScript"
        | "singleScript"
        | "scriptRunner"
        | "telematicMapping"
        | "suppressionScriptModal"
        | "customerReport"
        | "transportOrderCreateOrModify"
        | "reportScript"
        | "GeoStatusWorkbench"
        | "FtpSyncerPage"
        | "AiScripterPage"
        | "APIGatewayTester"
    responseLogs?: string[]
    autoUpdate?: boolean
    autoUpdateInterval?: number
}


export async function queryLogs(timespan: number, searchTerm: string) {
    let query = '"' + searchTerm + '"'
    if (searchTerm.includes(" OR ")) {
        let terms = searchTerm.split(" OR ")
        if (terms.length == 2) {
            query = '"' + terms[0] + '" OR "' + terms[1] + '"'
        }
    }

    let response = await grayLogApi.search(timespan, query)
    return response.cutMessages.reverse()
}

export function LogView(props: React.PropsWithChildren<LogViewProps>) {

    const context = React.useContext(ConfigContext);

    const [isLoading, setIsLoading] = useState(false);
    const [timespan, setTimespan] = useState(300);
    const [searchTerm, setSearchTerm] = useState("");
    const [response, setResponse] = useState(null);
    const [filterError, setFilterError] = useState(false);
    const [autoUpdate, setAutoUpdate] = useState(props.autoUpdate || false);
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const [grayLogStatusResponse, setGrayLogStatusResponse] = useState(null);
    const [logSource, setLogSource] = useState<"graylog" | "response">(props.responseLogs ? "response" : "graylog");

    const autoUpdateInterval = props.autoUpdateInterval ?? 5000;

    useEffect(() => {
        if (!props.query) {
            switch (props.origin) {
                case "exportSim": {
                    if (context.logSession.exportSimQuery) {
                        setSearchTerm(context.logSession.exportSimQuery);
                        setResponse(context.logSession.exportSimLogs);
                    }
                    break;
                }
                case "importSim": {
                    if (context.logSession.importSimQuery) {
                        setSearchTerm(context.logSession.importSimQuery);
                        setResponse(context.logSession.importSimLogs);
                    }
                    break;
                }
                case "transportOrderConfigTest": {
                    if (context.logSession.transportOrderConfigTestQuery) {
                        setSearchTerm(context.logSession.transportOrderConfigTestQuery);
                        setResponse(context.logSession.transportOrderConfigTestLogs);
                    }
                    break;
                }
                case "matrixDoc": {
                    if (context.logSession.matrixDocQuery) {
                        setSearchTerm(context.logSession.matrixDocQuery);
                        setResponse(context.logSession.matrixDocLogs);
                    }
                    break;
                }
                case "multiScript": {
                    if (context.logSession.multiScriptQuery) {
                        setSearchTerm(context.logSession.multiScriptQuery);
                        setResponse(context.logSession.multiScriptLogs);
                    }
                    break;
                }
                case "singleScript": {
                    if (context.logSession.singleScriptQuery) {
                        setSearchTerm(context.logSession.singleScriptQuery);
                        setResponse(context.logSession.singleScriptLogs);
                    }
                    break;
                }
                case "scriptRunner": {
                    if (context.logSession.scriptRunnerQuery) {
                        setSearchTerm(context.logSession.scriptRunnerQuery);
                        setResponse(context.logSession.scriptRunnerLogs);
                    }
                    break;
                }
                case "telematicMapping": {
                    if (context.logSession.telematicMappingQuery) {
                        setSearchTerm(context.logSession.telematicMappingQuery);
                        setResponse(context.logSession.telematicMappingLogs);
                    }
                    break;
                }
                case "customerReport": {
                    if (context.logSession.customerReportQuery) {
                        setSearchTerm(context.logSession.customerReportQuery);
                        setResponse(context.logSession.customerReportLogs);
                    }
                    break;
                }
                case "transportOrderCreateOrModify": {
                    if (context.logSession.transportOrderCreateOrModifyQuery) {
                        setSearchTerm(context.logSession.transportOrderCreateOrModifyQuery);
                        setResponse(context.logSession.transportOrderCreateOrModifyLogs);
                    }
                    break;
                }
                case "suppressionScriptModal": {
                    if (context.logSession.suppressionScriptModalQuery) {
                        setSearchTerm(context.logSession.suppressionScriptModalQuery);
                        setResponse(context.logSession.suppressionScriptModalLogs);
                    }
                    break;
                }
                case "reportScript": {
                    if (context.logSession.reportScriptQuery) {
                        setSearchTerm(context.logSession.reportScriptQuery);
                        setResponse(context.logSession.reportScriptLogs);
                    }
                    break;
                }
                case "GeoStatusWorkbench": {
                    if (context.logSession.geoStatusWorkbenchQuery) {
                        setSearchTerm(context.logSession.geoStatusWorkbenchQuery);
                        setResponse(context.logSession.geoStatusWorkbenchLogs);
                    }
                    break;
                }
                case "FtpSyncerPage": {
                    if (context.logSession.ftpSyncerPageQuery) {
                        setSearchTerm(context.logSession.ftpSyncerPageQuery);
                        setResponse(context.logSession.ftpSyncerPageLogs);
                    }
                    break;
                }
                case "AiScripterPage": {
                    if (context.logSession.aiScripterPageQuery) {
                        setSearchTerm(context.logSession.aiScripterPageQuery);
                        setResponse(context.logSession.aiScripterPageLogs);
                    }
                    break;
                }
                case "APIGatewayTester": {
                    if (context.logSession.apiGatewayTesterQuery) {
                        setSearchTerm(context.logSession.apiGatewayTesterQuery);
                        setResponse(context.logSession.apiGatewayTesterLogs);
                    }
                    break;
                }
            }
        } else {
            context.setSingleLogSession(props.origin, props.query, null)
        }
        if (props.initialTimespan) {
            setTimespan(props.initialTimespan)
        }
        getGraylogDelay().then((response) => {
            setGrayLogStatusResponse(response)
        })

        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
        }
    }, []);

    useEffect(() => {
        console.log("LogView: props.query changed to:", props.query, "searchTerm:", searchTerm);
        if (props.query && props.query !== searchTerm) {
            console.log("LogView: setting searchTerm to:", props.query);
            setSearchTerm(props.query);
            context.setSingleLogSession(props.origin, props.query, null);
            if (!props.autoUpdate) {
                searchLogs(props.query).then();
            }
        }
    }, [props.query]);

    useEffect(() => {
        console.log("LogView: updating logs");
        searchLogs().then()
    }, [logSource, props.responseLogs]);

    useEffect(() => {
        if (props.autoUpdate !== undefined) {
            setAutoUpdate(props.autoUpdate);
            if (props.autoUpdate && searchTerm) {
                console.log("Auto update enabled, starting auto updater with searchTerm:", searchTerm);
                if (intervalIdRef.current) {
                    console.log("Clearing existing interval before starting new one");
                    clearInterval(intervalIdRef.current);
                }
                // Sofort einmal ausführen
                searchLogs().then();
                // Dann Interval starten
                intervalIdRef.current = setInterval(() => {
                    console.log("Auto updating logs (from props)");
                    searchLogs().then();
                }, autoUpdateInterval);
                console.log("Created new interval:", intervalIdRef.current);
            } else {
                console.log("Auto update disabled, clearing interval");
                if (intervalIdRef.current) {
                    clearInterval(intervalIdRef.current);
                    intervalIdRef.current = null;
                }
            }
        }
    }, [props.autoUpdate, searchTerm]);

    async function searchLogs(st: string = searchTerm) {
        setIsLoading(true)

        let logs: string[]
        if(logSource == "graylog") {
            logs = await queryLogs(timespan, st)
        } else {
            logs = props.responseLogs
        }


        setResponse(logs)
        context.setSingleLogSession(props.origin, st, logs)
        console.log("responseMessage -> ", logs)
        setIsLoading(false)
    }

    function filterErrors() {
        if (!response) {
            return response;
        }

        let filteredResponse = response.filter((log: any) => {
            // Filtere Graylog Query Messages heraus
            const graylogQueryPattern = /Graylog Query is ".+" with range \d+/;
            if (graylogQueryPattern.test(log)) {
                return false;
            }
            return true;
        });

        if (filterError) {
            return filteredResponse.filter((log: any) => {
                return log.includes("ERROR")
            })
        }
        return filteredResponse;
    }

    function getInputStyle() {
        function isMax() {
            let maxEmails = [
                "m.foerster@logenios.com",
                "m.foerster@logenios.de"
            ]

            if (maxEmails.includes(authentication.user.email)) {
                return true
            }
        }

        if (isMax()) {
            return {
                width: "310px",
                marginLeft: "10px"
            }
        }
        return {
            flex: 1,
            marginLeft: "10px"
        }
    }

    return <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        <div
            id={"LogViewFilters"}
            style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "2px",
                flexShrink: 0
            }}>

            <Button.Group style={{ border: "1px solid #ccc", borderRadius: "4px", padding: "2px" }}>
                <Button
                    toggle
                    active={logSource === "graylog"}
                    onClick={() => setLogSource("graylog")}
                    basic={logSource !== "graylog"}
                    style={{
                        border: "1px solid #ccc",
                        fontSize: "12px",
                        padding: "4px 10px",
                        minHeight: "24px",
                        minWidth: "60px"
                    }}
                >
                    Graylog
                </Button>
                <Button
                    toggle
                    active={logSource === "response"}
                    onClick={() => setLogSource("response")}
                    basic={logSource !== "response"}
                    disabled={!props.responseLogs || props.responseLogs.length === 0}
                    style={{
                        border: "1px solid #ccc",
                        fontSize: "12px",
                        padding: "4px 10px",
                        minHeight: "24px",
                        minWidth: "60px"
                    }}
                >
                    Response
                </Button>
            </Button.Group>
            {logSource == "graylog" && <>
                <Input
                    placeholder='timespan'
                    value={timespan}
                    style={{ width: "100px" }}
                    onChange={(evt) => setTimespan(Number(evt.target.value))}
                />
                <Input
                    placeholder='searchterm'
                    value={searchTerm}
                    style={getInputStyle()}
                    onChange={(evt) => setSearchTerm(evt.target.value)}
                />

                <Button
                    loading={isLoading}
                    onClick={evt => searchLogs()}
                >
                    Search
                    {grayLogStatusResponse && <Popup
                        trigger={
                            <Icon
                                name={'signal'}
                                style={{
                                    color: getGraylogConnectionColorByMilliseconds(grayLogStatusResponse.delayInMs),
                                    marginLeft: "10px",
                                    marginRight: "10px"
                                }}
                            />
                        }
                        content={
                            <GrayLogStatus
                                previousResponse={grayLogStatusResponse}
                            />
                        }
                    />}
                </Button>
            </>}
            <span style={{paddingLeft: 5, float: "right"}}>
                    Filter Errors
                    <Checkbox
                        type='checkbox'
                        toggle
                        checked={filterError}
                        onChange={() => {
                            setFilterError(!filterError)
                        }}
                    />
                </span>
            <span style={{paddingLeft: 5, float: "right"}}>
                    Auto Update
                    <Checkbox
                        type='checkbox'
                        toggle
                        checked={autoUpdate}
                        onChange={() => {
                            const newAutoUpdate = !autoUpdate;
                            setAutoUpdate(newAutoUpdate);
                            if (newAutoUpdate) {
                                if(intervalIdRef.current) {
                                    clearInterval(intervalIdRef.current);
                                }
                                intervalIdRef.current = setInterval(() => {
                                    console.log("Auto updating logs (manual toggle)");
                                    searchLogs().then();
                                }, autoUpdateInterval);
                            } else {
                                if(intervalIdRef.current) {
                                    clearInterval(intervalIdRef.current);
                                    intervalIdRef.current = null;
                                }
                            }

                        }}
                    />
                </span>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
            <IfBox shouldShow={response != null}>
                <LogTextOutput logOutput={filterErrors()}/>
            </IfBox>
        </div>
    </div>
}